import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus, Loader2, Video } from 'lucide-react';
import { AdAccount } from '../../api/ads/ad-account.api';
import { fetchUrlImages, fetchUrlVideos } from '../../api/posts/fetch-post.api';
import { BrandBooking, BrandLiveRateCard, createBrandBooking } from '../../api/payment/brand-live.api';
import {
  BookingFormState,
  BOOKING_VALID_DAYS,
  bookingTotal,
  buildBookingBody,
  freeSeatsLabel,
  liveTotalPrice,
  validateBookingForm,
} from '../../utils/brand-live';
import { BRAND_CAMPAIGN_LIMITS } from '../../utils/brand-deal';
import DialogComponent from '../dialog/dialog.component';
import { MediaThumb } from '../brand-deal/brand-campaign-form';
import { useOrderFunding } from '../../hooks/use-order-funding';
import { approxCaLabel, fundingShortfall, ordererAccountLabel } from '../../utils/individual-order';
import { showFundingShortfall, showOrderErrorToast } from '../brand-deal/order-error-toast';
import BrandLivePreviewFrame from './brand-live-preview-frame';
import { useOrdererSanction } from '../brand-sanction/orderer-sanction-context';
import { SANCTION_LOCK_HINT } from '../../utils/brand-sanction';

// Hộp thoại doanh nghiệp đặt quảng cáo cho 1 creator: phần đăng bài và/hoặc phần livestream, một link đích
// chung cho cả đơn. Dùng cho đặt mới và "Đặt lại" (điền sẵn từ đơn cũ, vẫn sửa được).

const formatVnd = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')}đ`;

export const SHAPE_LABEL: Record<string, string> = {
  Corner: 'Góc màn hình',
  BottomBar: 'Dải phía dưới',
  Vertical: 'Cạnh phải',
};

const INPUT = 'mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text';

const Check: React.FC<{ checked: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }> = ({
  checked,
  disabled,
  onChange,
  label,
}) => (
  <label className={`flex items-center gap-2 text-sm font-semibold ui-text ${disabled ? 'opacity-50' : ''}`}>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
    {label}
  </label>
);

export interface BrandBookingDialogProps {
  creatorId: string;
  creatorName: string;
  rateCard: BrandLiveRateCard;
  bizAccounts: AdAccount[];
  /** Đơn cũ để điền sẵn khi "Đặt lại". */
  initial?: BrandBooking | null;
  onClose: () => void;
  onCreated: (b: BrandBooking) => void;
  onGoToRatingsTab?: () => void;
  /** Mở từ loại "Quảng cáo livestream" khi tạo chiến dịch: chọn sẵn phần livestream, bỏ chọn phần đăng bài. */
  liveFirst?: boolean;
}

const fromBooking = (
  b: BrandBooking | null | undefined,
  card: BrandLiveRateCard,
  creatorId: string,
  accountId: string,
  liveFirst = false
): BookingFormState => {
  const canPost = !!card.postPriceVnd;
  const canMonthly = !!card.liveMonthlyPriceVnd;
  const canHourly = !!card.liveHourlyPriceVnd;
  const canLive = (canMonthly || canHourly) && card.slots.length > 0;
  const slotOk = !!b?.live && card.slots.some((s) => s.id === b.live!.slotId && s.freeSeats > 0);
  const oldPlan = b?.live?.plan;
  const plan: 'Monthly' | 'Hourly' =
    oldPlan === 'Monthly' && canMonthly ? 'Monthly' : oldPlan === 'Hourly' && canHourly ? 'Hourly' : canMonthly ? 'Monthly' : 'Hourly';
  const oldHours = oldPlan === 'Hourly' ? Math.round(b!.live!.purchasedSeconds / 3600) : 0;
  return {
    creatorId,
    adAccountId: accountId,
    destinationUrl: b?.destinationUrl ?? '',
    withPost: canPost && (b ? !!b.post : !(liveFirst && canLive)),
    caption: b?.post?.caption ?? '',
    images: b?.post?.images ?? [],
    videos: b?.post?.videos ?? [],
    holdDays: b?.post?.holdDays ?? 14,
    withLive: canLive && (b ? !!b.live : liveFirst || !canPost),
    plan,
    hours: Math.max(oldHours, card.minHours),
    validDays: b?.live?.validDays ?? 30,
    slotId: slotOk ? b!.live!.slotId : '',
    logo: b?.live?.cardLogo ?? null,
    cardTitle: b?.live?.cardTitle ?? '',
    cardLine: b?.live?.cardLine ?? '',
  };
};

const BrandBookingDialog: React.FC<BrandBookingDialogProps> = ({
  creatorId,
  creatorName,
  rateCard,
  bizAccounts: allAccounts,
  initial,
  onClose,
  onCreated,
  onGoToRatingsTab,
  liveFirst = false,
}) => {
  const bizAccounts = useMemo(() => allAccounts.filter((a) => a.status === 'Verified'), [allAccounts]);
  const submitLock = useRef(false);
  // Đặt quảng cáo livestream bị chặn khi người đặt đang bị khoá tạo việc (RB8).
  const { blocked } = useOrdererSanction();
  const [accountId, setAccountId] = useState(bizAccounts[0]?.id ?? '');
  const selectedAccount = bizAccounts.find((a) => a.id === accountId) ?? null;
  const { funding } = useOrderFunding(selectedAccount);
  const [form, setForm] = useState<BookingFormState>(() =>
    fromBooking(initial, rateCard, creatorId, bizAccounts[0]?.id ?? '', liveFirst)
  );
  const [uploading, setUploading] = useState<'image' | 'video' | 'logo' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (patch: Partial<BookingFormState>) => setForm((f) => ({ ...f, ...patch }));
  const total = bookingTotal(form, rateCard);
  // Mỗi phần (bài đăng, livestream) là một đơn vị ký quỹ riêng, giống backend (R1).
  const escrowParts = [
    ...(form.withPost ? [{ unitVnd: rateCard.postPriceVnd ?? 0, count: 1 }] : []),
    ...(form.withLive ? [{ unitVnd: liveTotalPrice(rateCard, form.plan, form.hours), count: 1 }] : []),
  ];
  const canPost = !!rateCard.postPriceVnd;
  const canMonthly = !!rateCard.liveMonthlyPriceVnd;
  const canHourly = !!rateCard.liveHourlyPriceVnd;
  const canLive = (canMonthly || canHourly) && rateCard.slots.length > 0;
  const slot = rateCard.slots.find((s) => s.id === form.slotId);

  const previewAd = useMemo(
    () => ({
      logoUrl: form.logo?.url ?? '',
      title: form.cardTitle.trim() || 'Tiêu đề thẻ',
      line: form.cardLine.trim() || null,
      goUrl: 'https://cab.vn',
    }),
    [form.logo, form.cardTitle, form.cardLine]
  );

  const upload = async (kind: 'image' | 'video' | 'logo', files: FileList | null) => {
    if (!files || files.length === 0) return;
    try {
      setUploading(kind);
      if (kind === 'video') {
        const fd = new FormData();
        fd.append('type', files[0]);
        const res = (await fetchUrlVideos(fd)) as Array<{ id?: string; url?: string; status?: string; error?: string }>;
        const failed = res.filter((v) => v.status === 'Failed' || !v.id);
        if (failed.length) toast.error(failed[0]?.error || 'Tải video thất bại.');
        const ok = res.filter((v) => v.status !== 'Failed' && v.id).map((v) => ({ id: v.id as string, url: v.url as string }));
        if (ok.length) setForm((f) => ({ ...f, videos: [...f.videos, ...ok] }));
        return;
      }
      const remaining = kind === 'logo' ? 1 : BRAND_CAMPAIGN_LIMITS.maxImages - form.images.length;
      if (remaining <= 0) {
        toast.error(`Tối đa ${BRAND_CAMPAIGN_LIMITS.maxImages} ảnh.`);
        return;
      }
      const fd = new FormData();
      Array.from(files)
        .slice(0, remaining)
        .forEach((f) => fd.append('type', f));
      const res = ((await fetchUrlImages(fd)) as Array<{ id: string; url: string }>).map((r) => ({ id: r.id, url: r.url }));
      if (!res.length) return;
      if (kind === 'logo') setForm((f) => ({ ...f, logo: res[0] }));
      else setForm((f) => ({ ...f, images: [...f.images, ...res] }));
    } catch {
      toast.error(kind === 'video' ? 'Tải video thất bại.' : 'Tải ảnh thất bại.');
    } finally {
      setUploading(null);
    }
  };

  const submit = async () => {
    if (submitLock.current || submitting) return;
    const err = validateBookingForm(form, rateCard);
    if (err) {
      toast.error(err);
      return;
    }
    if (!accountId) {
      toast.error('Chọn tài khoản đặt đơn.');
      return;
    }
    const shortfall = fundingShortfall(funding, escrowParts, 'Số dư ví không đủ để ký quỹ deal này.');
    if (shortfall) {
      showFundingShortfall(funding, shortfall);
      return;
    }
    submitLock.current = true;
    try {
      setSubmitting(true);
      const created = await createBrandBooking({ ...buildBookingBody({ ...form, adAccountId: accountId }) });
      toast.success('Đã gửi đơn, chờ admin duyệt rồi tới creator trả lời.');
      onCreated(created);
    } catch (e) {
      showOrderErrorToast(e, 'Đặt quảng cáo thất bại.', onGoToRatingsTab);
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DialogComponent
      visible
      size="xl"
      title={`Đặt quảng cáo với ${creatorName}`}
      onClose={() => !submitting && onClose()}
      hideOnClickOutside={false}
    >
      <div className="max-h-[75vh] space-y-4 overflow-y-auto p-4">
        {bizAccounts.length > 1 && (
          <label className="block text-xs ui-muted">
            Tài khoản đặt đơn
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={INPUT}>
              {bizAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {ordererAccountLabel(a)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block text-xs ui-muted">
          Link đích cho cả đơn (bắt đầu bằng https://)
          <input
            value={form.destinationUrl}
            onChange={(e) => set({ destinationUrl: e.target.value })}
            placeholder="https://"
            className={INPUT}
          />
        </label>

        {/* Phần đăng bài */}
        <div className="space-y-2 rounded-lg border ui-divider p-3">
          <Check
            checked={form.withPost}
            disabled={!canPost}
            onChange={(v) => set({ withPost: v })}
            label={canPost ? `Đăng bài — ${formatVnd(rateCard.postPriceVnd ?? 0)}` : 'Đăng bài (creator chưa đặt giá)'}
          />
          {form.withPost && (
            <>
              <label className="block text-xs ui-muted">
                Nội dung bài
                <textarea
                  rows={4}
                  value={form.caption}
                  onChange={(e) => set({ caption: e.target.value })}
                  className={INPUT}
                />
              </label>
              <div className="flex flex-wrap gap-2">
                {form.images.map((img) => (
                  <MediaThumb
                    key={img.id || img.url}
                    url={img.url}
                    onRemove={() => setForm((f) => ({ ...f, images: f.images.filter((x) => x !== img) }))}
                  />
                ))}
                {form.images.length < BRAND_CAMPAIGN_LIMITS.maxImages && (
                  <label className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed ui-divider">
                    {uploading === 'image' ? <Loader2 size={18} className="animate-spin ui-muted" /> : <ImagePlus size={18} className="ui-muted" />}
                    <input type="file" accept="image/*" multiple hidden onChange={(e) => upload('image', e.target.files)} />
                  </label>
                )}
                {form.videos.map((v) => (
                  <MediaThumb
                    key={v.id || v.url}
                    url={v.url}
                    isVideo
                    onRemove={() => setForm((f) => ({ ...f, videos: f.videos.filter((x) => x !== v) }))}
                  />
                ))}
                {form.videos.length < BRAND_CAMPAIGN_LIMITS.maxVideos && (
                  <label className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-dashed ui-divider">
                    {uploading === 'video' ? <Loader2 size={18} className="animate-spin ui-muted" /> : <Video size={18} className="ui-muted" />}
                    <input type="file" accept="video/*" hidden onChange={(e) => upload('video', e.target.files)} />
                  </label>
                )}
              </div>
              <label className="block text-xs ui-muted">
                Số ngày giữ bài ({BRAND_CAMPAIGN_LIMITS.minHoldDays} đến {BRAND_CAMPAIGN_LIMITS.maxHoldDays})
                <input
                  type="number"
                  value={form.holdDays}
                  onChange={(e) => set({ holdDays: Number(e.target.value) })}
                  className={INPUT}
                />
              </label>
            </>
          )}
        </div>

        {/* Phần livestream */}
        <div className="space-y-3 rounded-lg border ui-divider p-3">
          <Check
            checked={form.withLive}
            disabled={!canLive}
            onChange={(v) => set({ withLive: v })}
            label={canLive ? 'Quảng cáo trên livestream' : 'Quảng cáo trên livestream (chưa có gói hoặc vị trí)'}
          />
          {form.withLive && (
            <>
              <div className="flex flex-wrap gap-4 text-sm ui-text">
                {canMonthly && (
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={form.plan === 'Monthly'} onChange={() => set({ plan: 'Monthly' })} />
                    Gói tháng: {rateCard.liveMonthlyHours} giờ, {formatVnd(rateCard.liveMonthlyPriceVnd ?? 0)}
                  </label>
                )}
                {canHourly && (
                  <label className="flex items-center gap-2">
                    <input type="radio" checked={form.plan === 'Hourly'} onChange={() => set({ plan: 'Hourly' })} />
                    Theo giờ: {formatVnd(rateCard.liveHourlyPriceVnd ?? 0)}/giờ
                  </label>
                )}
              </div>
              {form.plan === 'Hourly' && (
                <label className="block text-xs ui-muted">
                  Số giờ (tối thiểu {rateCard.minHours})
                  <input
                    type="number"
                    value={form.hours}
                    onChange={(e) => set({ hours: Number(e.target.value) })}
                    className={INPUT}
                  />
                </label>
              )}
              {form.plan === 'Hourly' && (
              <label className="block text-xs ui-muted">
                Thời hạn dùng hết số giờ
                <select value={form.validDays} onChange={(e) => set({ validDays: Number(e.target.value) })} className={INPUT}>
                  {BOOKING_VALID_DAYS.map((d) => (
                    <option key={d} value={d}>
                      {d} ngày
                    </option>
                  ))}
                </select>
              </label>
              )}
              <div>
                <div className="mb-1 text-xs ui-muted">Vị trí hiển thị</div>
                <div className="space-y-1">
                  {rateCard.slots.map((s) => (
                    <label
                      key={s.id}
                      className={`flex items-center gap-2 rounded-md border ui-divider px-3 py-2 text-sm ui-text ${s.freeSeats <= 0 ? 'opacity-50' : ''}`}
                    >
                      <input
                        type="radio"
                        disabled={s.freeSeats <= 0}
                        checked={form.slotId === s.id}
                        onChange={() => set({ slotId: s.id })}
                      />
                      <span className="font-medium">{s.name}</span>
                      <span className="ui-muted">
                        {SHAPE_LABEL[s.shape] ?? s.shape} · {freeSeatsLabel(s.freeSeats, s.maxAds)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                {form.logo ? (
                  <MediaThumb url={form.logo.url} onRemove={() => set({ logo: null })} />
                ) : (
                  <label className="flex size-20 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed ui-divider text-[11px] ui-muted">
                    {uploading === 'logo' ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
                    Logo
                    <input type="file" accept="image/*" hidden onChange={(e) => upload('logo', e.target.files)} />
                  </label>
                )}
                <div className="flex-1 space-y-2">
                  <input
                    value={form.cardTitle}
                    maxLength={32}
                    onChange={(e) => set({ cardTitle: e.target.value })}
                    placeholder="Tiêu đề thẻ (tối đa 32 ký tự)"
                    className={INPUT}
                  />
                  <input
                    value={form.cardLine}
                    maxLength={60}
                    onChange={(e) => set({ cardLine: e.target.value })}
                    placeholder="Dòng mô tả (không bắt buộc, tối đa 60 ký tự)"
                    className={INPUT}
                  />
                </div>
              </div>

              <div>
                <div className="mb-1 text-xs ui-muted">Xem trước{slot ? ` · ${SHAPE_LABEL[slot.shape]}` : ''}</div>
                <BrandLivePreviewFrame ad={previewAd} shape={slot?.shape ?? 'Corner'} />
              </div>
            </>
          )}
        </div>

        <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm ui-text dark:bg-slate-800/50">
          {form.withPost && <div>Đăng bài: {formatVnd(rateCard.postPriceVnd ?? 0)}</div>}
          {form.withLive && <div>Livestream: {formatVnd(liveTotalPrice(rateCard, form.plan, form.hours))}</div>}
          <div className="font-semibold">
            {funding.kind === 'Individual'
              ? `Tổng giá trị đơn: ${formatVnd(total)}, ${approxCaLabel(escrowParts, funding.vndPerCa)} nạp sẽ được giữ`
              : `Tổng tiền khoá trong ví: ${formatVnd(total)}`}
          </div>
        </div>

        {blocked && <p className="text-right text-[12px] text-rose-600 dark:text-rose-300">{SANCTION_LOCK_HINT}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            className="rounded-lg border ui-divider px-4 py-2 text-sm ui-text"
          >
            Huỷ
          </button>
          <button
            type="button"
            disabled={submitting || blocked}
            onClick={submit}
            className="rounded-lg bg-[#0057FF] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting ? 'Đang gửi…' : 'Xác nhận đặt'}
          </button>
        </div>
      </div>
    </DialogComponent>
  );
};

export default BrandBookingDialog;
